import { unaryRpc } from "@redotech/rpc/definition";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/**
 * Truly team-specific top-level properties (identity, tokens, billing,
 * addresses, live counters) excluded from copying by default but selectable.
 */
export const DEFAULT_EXCLUDED_TEAM_PROPERTIES: readonly string[] = [
  "name",
  "displayName",
  "email",
  "domain",
  "storeUrl",
  "storePassword",
  "widget_slug",
  "accountId",
  "users",
  "adminName",
  "adminPhone",
  "ipAddress",
  "installSource",
  "branded_resale",
  "storefrontAccessToken",
  "headlessAccessToken",
  "intercomId",
  "slackChannelId",
  "slackChannelIds",
  "stripe",
  "connectedStripeAccountId",
  "billingMethod",
  "billingAutomation",
  "billingFailure",
  "failedBillingEmails",
  "manualBillingDueInDays",
  "shopifyReturnsBillingRequiredSince",
  "pricePerOrder",
  "merchantPricePerOrder",
  "_shopify",
  "address",
  "publicAddress",
  "customInStoreAddresses",
  "returnLocationId",
  "currentExternalReturnId",
  "currentFulfillmentBatchId",
  "teamAnonymization",
  "eventDerivedState",
  "metricsSnapshot",
  "dealSizing",
  "createdAt",
  "redoInternalProducts",
  "fulfillmentBreakdown",
];

export const objectIdMode = z.enum([
  /**
   * Fresh independent `_id` per copied subdocument, replaced in place (string
   * or ObjectId form preserved); references are NOT rewritten.
   */
  "none",
  /** Copy source ObjectIds verbatim. */
  "copy",
  /**
   * Mint a new ObjectId per distinct source id and use it everywhere that id
   * appears in the copied values, preserving internal references.
   */
  "regenerate",
]);
export type ObjectIdMode = z.infer<typeof objectIdMode>;

export const teamCopyChangeKind = z.enum([
  /** A real value difference. */
  "value",
  /** Arrays with the same elements (ignoring subdocument _ids) in a different order. */
  "reorder",
  /** Values identical except for subdocument ObjectId `_id` fields. */
  "id",
]);
export type TeamCopyChangeKind = z.infer<typeof teamCopyChangeKind>;

export const teamCopyChange = z.object({
  /** Dot-path of the field on the team document. */
  path: z.string(),
  /** First segment of the path, used for grouping and selection. */
  topLevelProperty: z.string(),
  kind: teamCopyChangeKind,
  /** Destination's current value, JSON-stringified (empty string if unset). */
  before: z.string(),
  /** Source's value that would be written, JSON-stringified (empty if unset). */
  after: z.string(),
});
export type TeamCopyChange = z.infer<typeof teamCopyChange>;

export const copyTeamSettings = unaryRpc({
  name: "copyTeamSettings",
  input: z.object({
    sourceTeamId: zExt.objectId(),
    destinationTeamId: zExt.objectId(),
    /** When true, compute the full diff but do not write anything. */
    dryRun: z.boolean(),
    /** Exact change paths to apply. Ignored on dryRun (full diff always returned). */
    paths: z.array(z.string()).optional(),
    /** How subdocument ObjectIds on copied values are handled. */
    objectIdMode,
  }),
  output: z.object({
    success: z.boolean(),
    message: z.string(),
    sourceTeamName: z.string().optional(),
    destinationTeamName: z.string().optional(),
    /** Only fields whose value actually differs between the two teams. */
    changes: z.array(teamCopyChange),
    /**
     * Full destination team document as it was BEFORE applying,
     * JSON-stringified; feed it to restoreTeamBackup to revert.
     */
    backup: z.string().optional(),
    /**
     * On dryRun with `paths`: the destination document as it WOULD look after
     * applying the selected changes, JSON-stringified. Nothing is written.
     */
    simulatedDocument: z.string().optional(),
  }),
});

export const restoreTeamBackup = unaryRpc({
  name: "restoreTeamBackup",
  input: z.object({
    teamId: zExt.objectId(),
    /** A backup file produced by copyTeamSettings (full team document JSON). */
    backupJson: z.string(),
  }),
  output: z.object({
    success: z.boolean(),
    message: z.string(),
    teamName: z.string().optional(),
    /** The team document as it was BEFORE the restore, JSON-stringified. */
    preRestoreBackup: z.string().optional(),
  }),
});
